import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'
import { SchedulablePresenter, OutletPresenter } from 'shared/presenters'
import useTranslation from 'next-translate/useTranslation'
import Header from 'library/molecules/Header'
import Layout from 'library/atoms/Layout'
import Head from 'next/head'
import { H4, Text } from 'library/atoms/Typography'
import { BackBreadcrumb } from 'library/molecules/Breadcrumb'
import { Formik, Form } from 'formik'
import Input, { InputTelephone } from 'library/atoms/Input'
import Flex from 'shared/library/atoms/Flex'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import FieldHolder, { FieldTitle } from 'library/atoms/FieldHolder'
import ContentRadio from 'library/atoms/RadioButton/ContentRadio'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import { TIME_REGEX } from 'shared/enums/regex'
import { defined } from 'shared/presenters/presenter'
import BottomBar, { BottomBarButton } from 'library/atoms/BottomBar/BottomBar'
import { AddABookingValues } from 'helpers/generateInitialValuesForActivity'
import { useFormikContext } from 'formik'
import useRequestTimeSlot from 'state/useRequestTimeSlot'
import ModalField from 'library/molecules/ModalField/ModalField'
import useModal from 'shared/state/useModal'
import ActivityRequestTimeSlotModals, {
  ModalProps,
  MODAL_CONFIG
} from './ActivityRequestTimeSlotModals'
import InfoBar from 'library/molecules/InfoBar/InfoBar'
import Quantity from 'library/molecules/Quantity'
import { yupValidateTelephone } from 'shared/helpers/yupValidation'

dayjs.extend(utc)

interface ActivityRequestTimeSlotProps {
  outlet: OutletPresenter
  activity: SchedulablePresenter
  goToDetails: () => void
  view?: string
}

interface RequestTimeSlotFields {
  adultsGoing: number
  childrenGoing: number
  organizerParticipating: string
  contactNumber: string
  enthusiastName: string
  enthusiastEmail: string
  startDate: string
  startTime: string
}

const generateInitialValuesForRequest = ({
  adultsGoing,
  childrenGoing,
  organizerParticipating = ''
}: {
  adultsGoing: number
  childrenGoing: number
  organizerParticipating?: string
}): RequestTimeSlotFields => ({
  adultsGoing,
  childrenGoing,
  organizerParticipating,
  contactNumber: '',
  enthusiastName: '',
  enthusiastEmail: '',
  startDate: '',
  startTime: ''
})

const ActivityRequestTimeSlot = ({
  outlet,
  activity,
  goToDetails
}: ActivityRequestTimeSlotProps) => {
  const { t } = useTranslation('chargeable')
  const { query } = useRouter()
  const widgetSlug = query.widgetSlug as string
  const [selectedDate, setSelectedDate] = useState<Dayjs>()
  const errorInfoRef = useRef<HTMLDivElement>(null)
  /**
   * Bear in mind, this component has an internal Formik, which we use to request a timeslot, but it also
   * has access to the Formik used by the Activity page to configure the booking. We want to pull the values
   * from the config view so the user doesn't have to enter them again here.
   */
  const {
    values: {
      adultsGoing: configAdultsGoing,
      childrenGoing: configChildrenGoing,
      organizerParticipating: configOrganizerParticipating
    }
  } = useFormikContext<AddABookingValues>()

  const peopleGoing = configAdultsGoing + configChildrenGoing

  const requiredMessage = t('common:required')

  const RequestFormValidateSchema = Yup.object().shape({
    enthusiastName: Yup.string()
      .typeError(t('invalid-name'))
      .required(requiredMessage),

    contactNumber: yupValidateTelephone(t).required(requiredMessage),

    enthusiastEmail: Yup.string().email().required(requiredMessage),

    organizerParticipating: Yup.boolean().required(requiredMessage),

    startDate: Yup.string().required(requiredMessage),

    startTime: Yup.string()
      .matches(TIME_REGEX, t('invalid-time'))
      .required(requiredMessage)
  })

  // Make next year available to choose from
  const availableDays = Array(365)
    .fill(0)
    .map((_x, i) => dayjs().add(i, 'days').toDate())

  const { activeModal, setActiveModalId, currentModalProps } =
    useModal<ModalProps>({
      MODAL_CONFIG,
      modalPropVariations: {
        calendar: {
          name: 'calendar',
          availableDays,
          onDateChange: (date?: Dayjs) => {
            setSelectedDate(date)
            setActiveModalId(null)
          }
        },
        requestSuccess: {
          goToDetails,
          outlet
        }
      }
    })

  const { requestTimeSlot, requestTimeSlotError, requestTimeSlotLoading } =
    useRequestTimeSlot({
      onCompleted: () => setActiveModalId(MODAL_CONFIG.requestSuccess.id)
    })

  const errorMessage = requestTimeSlotError?.message

  const onSubmit = async (values: RequestTimeSlotFields) => {
    try {
      await requestTimeSlot({
        variables: {
          schedulableId: defined(activity.id),
          peopleGoing: values.adultsGoing + values.childrenGoing,
          childrenGoing: values.childrenGoing,
          organizerParticipating: values.organizerParticipating,
          contactNumber: values.contactNumber,
          enthusiastName: values.enthusiastName,
          enthusiastEmail: values.enthusiastEmail,
          startDate: dayjs(values.startDate).format('DD-MM-YYYY'),
          startTime: values.startTime,
          widgetSlug: widgetSlug
        }
      })
    } catch (err) {
      errorInfoRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <Head>
        <title>{t('chargeable:request-a-time')}</title>
      </Head>
      <Formik
        initialValues={generateInitialValuesForRequest({
          adultsGoing: configAdultsGoing,
          childrenGoing: configChildrenGoing,
          organizerParticipating: !activity.adultsAllowed()
            ? 'false'
            : configOrganizerParticipating
        })}
        onSubmit={onSubmit}
        validationSchema={RequestFormValidateSchema}
      >
        {({ handleSubmit, values: requestTimeSlotValues, isValid, dirty }) => (
          <Layout>
            <Header />
            <BackBreadcrumb
              title={t('chargeable:request-a-time')}
              onClick={goToDetails}
            />
            <main>
              <H4 mb={4}>{t('chargeable:request-a-time')}</H4>
              <Text>{t('chargeable:request-explanation')}</Text>
              <StyledForm>
                {/*
                 * Date and time
                 */}
                <FieldHolder hasValues={!!selectedDate} mb={4} withBoxShadow>
                  <FieldTitle required>
                    {t('chargeable:preferred-date-time')}
                  </FieldTitle>
                  <FieldHolder hasValues={!!selectedDate} mb={4}>
                    <FieldTitle required>
                      {t('chargeable:preferred-date')}
                    </FieldTitle>
                    <ModalField
                      onClick={() => setActiveModalId(MODAL_CONFIG.calendar.id)}
                      hasValues={!!selectedDate}
                    >
                      <Text fontStyle={'h6'}>
                        {selectedDate
                          ? selectedDate.format('ddd D MMMM')
                          : t('choose-date-here')}
                      </Text>
                    </ModalField>
                  </FieldHolder>
                  <FieldHolder
                    hasValues={!!requestTimeSlotValues.startTime}
                    mb={4}
                  >
                    <FieldTitle required>
                      {t('chargeable:preferred-time')}
                    </FieldTitle>
                    <Input placeholder="e.g. 14:00 " name="startTime" />
                  </FieldHolder>
                </FieldHolder>
                {/*
                 * People going
                 */}
                <FieldHolder
                  mb={4}
                  mt={4}
                  hasValues={peopleGoing > 0}
                  withBoxShadow
                >
                  <FieldTitle required>{t('how-many-going')}</FieldTitle>
                  {activity.adultsAllowed() && (
                    <StyledPeopleGoingFieldHolder>
                      <Text fontStyle="h5">{t('common:adults')}</Text>
                      <Quantity name="adultsGoing" min={0} max={50} />
                    </StyledPeopleGoingFieldHolder>
                  )}
                  {activity.childrenAllowed() && (
                    <StyledPeopleGoingFieldHolder>
                      <Text fontStyle="h5">{t('common:children')}</Text>
                      <Quantity name="childrenGoing" min={0} max={50} />
                    </StyledPeopleGoingFieldHolder>
                  )}
                </FieldHolder>
                {/*
                 * Organizer Participating
                 */}
                {activity.adultsAllowed() && (
                  <FieldHolder
                    mb={4}
                    hasValues={!!requestTimeSlotValues.organizerParticipating}
                    withBoxShadow
                  >
                    <FieldTitle required>
                      {t('chargeable:taking-part-yourself')}
                    </FieldTitle>
                    <Flex>
                      <ContentRadio
                        name={'organizerParticipating'}
                        value={'true'}
                        id="participating-true"
                        mr={3}
                      >
                        <label htmlFor="participating-true">
                          {t('common:yes')}
                        </label>
                      </ContentRadio>
                      <ContentRadio
                        name={'organizerParticipating'}
                        value={'false'}
                        id="participating-false"
                      >
                        <label htmlFor="participating-false">
                          {t('common:no')}
                        </label>
                      </ContentRadio>
                    </Flex>
                  </FieldHolder>
                )}
                {/*
                 * Contact details
                 */}
                <FieldHolder mb={4} withBoxShadow>
                  <FieldTitle required>
                    {t('checkout:contact-details')}
                  </FieldTitle>
                  <Input
                    placeholder={t('checkout:full-name')}
                    name={'enthusiastName'}
                    autoComplete="name"
                  />
                  <InputTelephone
                    name={'contactNumber'}
                    placeholder={t('common:phone-number')}
                    defaultCountry={activity.address?.country}
                  />
                  <Input
                    placeholder={t('checkout:email-address')}
                    type="email"
                    name={'enthusiastEmail'}
                    autoComplete="email"
                  />
                </FieldHolder>
                {errorMessage && (
                  <InfoBar
                    icon={'close'}
                    variant={'error'}
                    ref={errorInfoRef}
                    mt={3}
                  >
                    {t('chargeable:alert-problem')} {errorMessage}
                  </InfoBar>
                )}
              </StyledForm>
            </main>
            <BottomBar>
              <BottomBarButton
                type="submit"
                width={1}
                onClick={handleSubmit}
                loading={requestTimeSlotLoading}
                inactive={!(isValid && dirty)}
              >
                {t('chargeable:send-request')}
              </BottomBarButton>
            </BottomBar>
            <ActivityRequestTimeSlotModals
              activeModal={activeModal}
              {...currentModalProps}
            />
          </Layout>
        )}
      </Formik>
    </>
  )
}

export default ActivityRequestTimeSlot

const StyledForm = styled(Form)(({ theme }) => ({
  marginTop: theme.space[8],
  marginBottom: theme.space[8]
}))

const StyledPeopleGoingFieldHolder = styled(FieldHolder)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: 'auto',
  padding: theme.space[4],
  marginBottom: theme.space[4]
}))
